/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect } from 'react';
import { BlockSwitch } from '@/shared/ui/components/composed/settings-block';
import { cn } from '@/shared/ui/utils/tailwind';
import { GearIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';

/**
 * The on/off switch, now the design system's `BlockSwitch`.
 *
 * Kept under the old name and signature so `PortalListsSection` — a screen
 * this redesign does not touch — keeps working without a rename.
 *
 * @param props - The toggle props.
 * @param props.checked - Whether the toggle is on.
 * @param props.onChange - Callback invoked with the next checked value.
 * @param props.disabled - Whether the toggle is disabled.
 * @returns The toggle switch element.
 */
export function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <BlockSwitch checked={checked} onChange={onChange} disabled={disabled} />;
}

/**
 * Renders a button that copies text to the clipboard with transient feedback.
 *
 * @param props - The copy button props.
 * @param props.text - The text to copy to the clipboard.
 * @param props.label - The default button label.
 * @param props.copiedLabel - The label shown briefly after copying.
 * @param props.className - Optional extra CSS classes.
 * @returns The copy button element.
 */
export function CopyButton({ text, label, copiedLabel, className: extraClass }: { text: string; label: string; copiedLabel: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  return (
    <UnstyledButton
      onClick={handleCopy}
      className={cn(
        'px-(--space-3) py-(--space-1-5) text-xs rounded-(--radius-sm) border cursor-pointer transition-colors duration-200',
        copied
          ? 'border-[var(--status-success-border)] bg-[var(--status-success-bg)] text-[var(--status-success-text)] font-medium'
          : 'border-(--border-subtle) bg-(--surface-secondary) hover:bg-(--surface-primary) text-(--text-primary)',
        extraClass,
      )}
    >
      {copied ? copiedLabel : label}
    </UnstyledButton>
  );
}

/**
 * The "Configurações" button of a connected portal card: opens the portal's
 * settings modal, so the card itself stays one row.
 *
 * @param props - The click handler, label and test id.
 * @returns The button.
 */
export function ConfigButton({ onClick, label, testId }: { onClick: () => void; label: string; testId: string }) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-haspopup="dialog"
      data-testid={testId}
      className={cn(
        'ml-auto inline-flex items-center gap-(--space-1-5) text-xs font-semibold px-(--space-3) py-(--space-1-5)',
        'rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-primary) cursor-pointer transition-colors',
        'text-(--text-secondary) hover:text-(--text-primary)',
      )}
    >
      <GearIcon aria-hidden="true" />
      {label}
    </UnstyledButton>
  );
}

/**
 * Renders an animated top progress bar while a save is in flight.
 *
 * @param props - The saving bar props.
 * @param props.visible - Whether a save operation is currently active.
 * @returns The animated saving bar element.
 */
export function SavingBar({ visible }: { visible: boolean }) {
  const [show, setShow] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (visible) {
      setShow(true);
      setFinishing(false);
    } else if (show) {
      setFinishing(true);
    }
  }, [visible]);

  const handleAnimationIteration = () => {
    if (finishing) {
      setShow(false);
      setFinishing(false);
    }
  };

  return (
    <div className="-mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-(--space-4) sm:mb-(--space-6) h-[2px] overflow-hidden">
      {show && (
        <div
          onAnimationIteration={handleAnimationIteration}
          className="h-full w-1/3 rounded-full"
          style={{
            background: 'var(--seed-base)',
            animation: 'saving-slide 1.2s ease-in-out infinite',
          }}
        />
      )}
      <style>{`
        @keyframes saving-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

/**
 * The portal settings could not be read: say so and offer a retry, instead
 * of drawing the cards over settings we do not know.
 *
 * @param props - Retry action and translation function.
 * @returns The error notice.
 */
export function SettingsLoadError({ onRetry, t }: { onRetry: () => void; t: (key: string) => string }) {
  return (
    <div
      className="px-(--space-4) py-(--space-3) rounded-(--radius-md) bg-[var(--status-warning-bg)] border border-[var(--status-warning-border)] flex items-center justify-between gap-(--space-3)"
      data-testid="portal-settings-load-error"
    >
      <Text component="span" className="text-sm text-[var(--status-warning-text)]">{t('portal.erroCarregarConexoes')}</Text>
      <UnstyledButton
        onClick={onRetry}
        data-testid="portal-settings-retry"
        className="shrink-0 text-xs font-semibold px-(--space-3) py-(--space-1-5) rounded-(--radius-sm) border border-(--border-subtle) bg-[var(--surface-primary)] text-(--text-primary) cursor-pointer"
      >
        {t('portal.tentarNovamente')}
      </UnstyledButton>
    </div>
  );
}
